/**
 * 地面居民：跟地形、有朝向、閒逛、避開水與陡坡，按性情對遊客反應。
 */

import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { useGameStore } from '../core/store/gameStore'
import { playerPose } from '../core/playerPose'
import { terrainBuffer } from '../core/terrainBuffer'
import { TERRAIN_WORLD_SIZE, WATER_LEVEL } from '../core/terrainConstants'
import { BeastDoll } from './BeastDoll'
import { PlantDoll } from './PlantDoll'
import { STUDIO_XZ, studioHeight } from './StudioStage'
import type { Placement, Species } from '../content/types'

const WALK_SPEED = 3.2
const FLEE_SPEED = 7
const APPROACH_SPEED = 4
const NOTICE = 16
const MEET = 7
const HALF = TERRAIN_WORLD_SIZE * 0.42
const MAX_SLOPE = 1.15

export function walkable(x: number, z: number): boolean {
  if (Math.abs(x) > HALF || Math.abs(z) > HALF) return false
  const y = terrainBuffer.getHeight(x, z)
  if (y <= WATER_LEVEL + 1.2) return false
  const y2 = terrainBuffer.getHeight(x + 1.2, z)
  if (Math.abs(y2 - y) / 1.2 > MAX_SLOPE) return false
  return true
}

function pickWander(x: number, z: number): [number, number] {
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2
    const d = 8 + Math.random() * 22
    const nx = x + Math.cos(a) * d
    const nz = z + Math.sin(a) * d
    if (walkable(nx, nz)) return [nx, nz]
  }
  return [x, z]
}

function Inhabitant({
  placement,
  species,
  visitMode,
}: {
  placement: Placement
  species: Species
  visitMode: boolean
}) {
  const group = useRef<THREE.Group>(null)
  const walking = useRef(false)
  const heading = useRef(0)
  const x = useRef(placement.position[0])
  const z = useRef(placement.position[2])
  const tx = useRef(placement.position[0])
  const tz = useRef(placement.position[2])
  const met = useRef(false)
  const retarget = useRef(0)

  useFrame((_, rawDt) => {
    if (!terrainBuffer.ready || !group.current) return
    const dt = Math.min(rawDt, 0.08)
    const px = playerPose.x
    const pz = playerPose.z
    const st = useGameStore.getState()
    const guest = st.editorMode === 'walk' && st.isEnteredWorld

    const dxP = px - x.current
    const dzP = pz - z.current
    const distP = Math.hypot(dxP, dzP)

    let speed = WALK_SPEED
    if (guest && distP < NOTICE) {
      if (species.temperament === 'flee') {
        const len = distP || 1
        tx.current = x.current - (dxP / len) * 18
        tz.current = z.current - (dzP / len) * 18
        speed = FLEE_SPEED
      } else if (species.temperament === 'approach') {
        tx.current = px
        tz.current = pz
        speed = APPROACH_SPEED
        if (distP < 3.5) {
          tx.current = x.current
          tz.current = z.current
        }
      } else if (species.temperament === 'call') {
        tx.current = x.current
        tz.current = z.current
        heading.current = Math.atan2(dxP, dzP)
      }
      if (distP < MEET && !met.current) {
        met.current = true
        const msg = species.encounter === 'message'
          ? (species.encounterMessage || `${species.name} 在此。`)
          : species.encounter === 'trace'
            ? `曾見${species.name}`
            : species.temperament === 'call'
              ? `${species.name}鳴。`
              : `遇見${species.name}`
        st.setEncounterToast(msg)
        if (visitMode) {
          st.addSighting({
            visitorId: 'guest',
            hostWorldId: st.worldId,
            speciesId: species.id,
            at: new Date().toISOString(),
          })
        }
      }
    } else {
      retarget.current -= dt
      if (retarget.current <= 0) {
        const [nx, nz] = pickWander(x.current, z.current)
        tx.current = nx
        tz.current = nz
        retarget.current = 3 + Math.random() * 4
      }
    }

    const dx = tx.current - x.current
    const dz = tz.current - z.current
    const dist = Math.hypot(dx, dz)
    const hold = guest && species.temperament === 'call' && distP < NOTICE
    if (dist > 0.4 && !hold) {
      const step = Math.min(speed * dt, dist)
      const nx = x.current + (dx / dist) * step
      const nz = z.current + (dz / dist) * step
      if (walkable(nx, nz)) {
        x.current = nx
        z.current = nz
        heading.current = Math.atan2(dx, dz)
        walking.current = true
      } else {
        walking.current = false
        retarget.current = 0
      }
    } else {
      walking.current = false
    }

    const y = Math.max(terrainBuffer.getHeight(x.current, z.current), WATER_LEVEL)
    group.current.position.set(x.current, y, z.current)
  })

  const y0 = Math.max(terrainBuffer.getHeight(placement.position[0], placement.position[2]), WATER_LEVEL)

  return (
    <group ref={group} position={[placement.position[0], y0, placement.position[2]]}>
      {species.kind === 'plant'
        ? <PlantDoll parts={species.parts} form={species.plantForm} />
        : <BeastDoll parts={species.parts} form={species.beastForm} walkingRef={walking} headingRef={heading} />}
    </group>
  )
}

function PlantResident({
  placement,
  species,
  visitMode,
}: {
  placement: Placement
  species: Species
  visitMode: boolean
}) {
  const group = useRef<THREE.Group>(null)
  const met = useRef(false)
  const x = placement.position[0]
  const z = placement.position[2]

  useFrame(() => {
    if (!terrainBuffer.ready || !group.current) return
    const st = useGameStore.getState()
    const guest = st.editorMode === 'walk' && st.isEnteredWorld
    if (!guest) return
    const distP = Math.hypot(playerPose.x - x, playerPose.z - z)
    if (distP < MEET && !met.current) {
      met.current = true
      const msg = species.encounter === 'message'
        ? (species.encounterMessage || `${species.name} 在此。`)
        : `遇見${species.name}`
      st.setEncounterToast(msg)
      if (visitMode) {
        st.addSighting({
          visitorId: 'guest',
          hostWorldId: st.worldId,
          speciesId: species.id,
          at: new Date().toISOString(),
        })
      }
    }
  })

  const y0 = Math.max(terrainBuffer.getHeight(x, z), WATER_LEVEL)
  return (
    <group ref={group} position={[x, y0, z]}>
      <PlantDoll parts={species.parts} form={species.plantForm} />
    </group>
  )
}

export function Inhabitants() {
  const placements = useGameStore(s => s.placements)
  const species = useGameStore(s => s.species)
  const visitMode = useGameStore(s => s.visitMode)
  const byId = new Map(species.map(s => [s.id, s]))

  return (
    <>
      {placements.map(p => {
        const sp = byId.get(p.speciesId)
        if (!sp) return null
        return sp.kind === 'plant'
          ? <PlantResident key={p.id} placement={p} species={sp} visitMode={visitMode} />
          : <Inhabitant key={p.id} placement={p} species={sp} visitMode={visitMode} />
      })}
    </>
  )
}

/** 製造器預覽：作坊時站石上可轉看；否則在島上走一圈 */
export function DraftPreview({ species }: { species: Species }) {
  const group = useRef<THREE.Group>(null)
  const walking = useRef(true)
  const heading = useRef(0)
  const ang = useRef(0)
  const studio = useGameStore(s => s.makerOpen && s.makerKind === 'beast' && species.kind !== 'plant')
  const studioWalk = useGameStore(s => s.studioWalk)

  useFrame((_, dt) => {
    if (!terrainBuffer.ready || !group.current) return
    if (species.kind === 'plant') {
      const x = 10
      const z = 8
      const px = walkable(x, z) ? x : 0
      const pz = walkable(x, z) ? z : 0
      group.current.position.set(px, Math.max(terrainBuffer.getHeight(px, pz), WATER_LEVEL), pz)
      walking.current = false
      return
    }
    if (studio) {
      const y = studioHeight() + 0.32
      group.current.position.set(STUDIO_XZ.x, y, STUDIO_XZ.z)
      heading.current = 0.55
      walking.current = studioWalk
      return
    }
    ang.current += dt * 0.35
    const r = 14
    const x = Math.cos(ang.current) * r
    const z = Math.sin(ang.current) * r
    if (!walkable(x, z)) {
      group.current.position.set(0, Math.max(terrainBuffer.getHeight(0, 0), WATER_LEVEL), 0)
      walking.current = false
      return
    }
    walking.current = true
    heading.current = ang.current + Math.PI / 2
    const y = Math.max(terrainBuffer.getHeight(x, z), WATER_LEVEL)
    group.current.position.set(x, y, z)
  })

  return (
    <group ref={group}>
      {species.kind === 'plant'
        ? <PlantDoll parts={species.parts} form={species.plantForm} />
        : <BeastDoll parts={species.parts} form={species.beastForm} walkingRef={walking} headingRef={heading} />}
    </group>
  )
}
